import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, IconButton, List, ListItem,
    ListItemSecondaryAction, Button, Chip, CircularProgress, Tooltip,
    Tabs, Tab
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useNavigate } from 'react-router-dom';
import {
    notificacionesListarJWT,
    notificacionMarcarLeidaJWT,
    notificacionesMarcarTodasLeidasJWT,
    notificacionEliminarJWT
} from '../api/endpoints';
import { tiempoRelativo } from '../utils/fechas';

const useStyles = makeStyles((theme) => ({
    drawer: {
        width: 400,
        [theme.breakpoints.down('md')]: {
            width: '100%'
        }
    },
    header: {
        padding: theme.spacing(2),
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: theme.palette.primary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    content: {
        padding: theme.spacing(1.5),
        overflowY: 'auto',
        flex: 1,
    },
    notifItem: {
        borderRadius: 4,
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1.5),
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            background: '#f0f7ff'
        }
    },
    notifNoLeida: {
        background: '#e3f2fd',
        borderLeft: `4px solid ${theme.palette.primary.main}`
    },
    notifLeida: {
        background: '#f8fafc',
        opacity: 0.7,
        '&:hover': {
            opacity: 1,
        }
    },
    iconBox: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(1.5)
    },
    empty: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary
    }
}));

const NotificacionesPanel = ({ open, onClose }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [notificaciones, setNotificaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [noLeidas, setNoLeidas] = useState(0);
    const [tabActivo, setTabActivo] = useState(0); // 0 = no leídas, 1 = historial

    useEffect(() => {
        if (open) {
            cargarNotificaciones();
            setTabActivo(0);
        }
    }, [open]);

    const cargarNotificaciones = async () => {
        setLoading(true);
        try {
            const response = await notificacionesListarJWT();
            if (response?.ok) {
                setNotificaciones(response.notificaciones || []);
                setNoLeidas(response.noLeidas || 0);
            }
        } catch (err) {
            console.error('Error al cargar notificaciones:', err);
        } finally {
            setLoading(false);
        }
    };

    const pendientes = notificaciones.filter(n => !n.leida);
    const historial = notificaciones.filter(n => n.leida);

    const marcarLeida = async (id) => {
        try {
            const response = await notificacionMarcarLeidaJWT(id);
            if (response?.ok) {
                setNotificaciones(prev =>
                    prev.map(n => n.id === id ? { ...n, leida: true } : n)
                );
                setNoLeidas(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Error al marcar como leída:', err);
        }
    };

    const marcarTodasLeidas = async () => {
        try {
            const response = await notificacionesMarcarTodasLeidasJWT();
            if (response?.ok) {
                setNotificaciones(prev =>
                    prev.map(n => ({ ...n, leida: true }))
                );
                setNoLeidas(0);
            }
        } catch (err) {
            console.error('Error al marcar todas como leídas:', err);
        }
    };

    const eliminarNotificacion = async (e, id) => {
        e.stopPropagation();
        try {
            const response = await notificacionEliminarJWT(id);
            if (response?.ok) {
                const notif = notificaciones.find(n => n.id === id);
                setNotificaciones(prev => prev.filter(n => n.id !== id));
                if (notif && !notif.leida) {
                    setNoLeidas(prev => Math.max(0, prev - 1));
                }
            }
        } catch (err) {
            console.error('Error al eliminar notificación:', err);
        }
    };

    const handleClickNotificacion = (notif) => {
        if (!notif.leida) {
            marcarLeida(notif.id);
        }
        if (notif.link) {
            navigate(notif.link);
            onClose();
        }
    };

    const getIconByType = (tipo) => {
        const iconProps = { style: { fontSize: 24 } };
        switch (tipo) {
            case 'success':
                return { icon: <CheckCircleIcon {...iconProps} />, color: '#4caf50', bg: '#e8f5e9' };
            case 'warning':
                return { icon: <WarningIcon {...iconProps} />, color: '#ff9800', bg: '#fff3e0' };
            case 'error':
                return { icon: <ErrorIcon {...iconProps} />, color: '#f44336', bg: '#ffebee' };
            case 'info':
            default:
                return { icon: <InfoIcon {...iconProps} />, color: '#2196f3', bg: '#e3f2fd' };
        }
    };

    // Usa el helper compartido — el backend devuelve fechas que requieren
    // parseo especial para evitar el shift de TZ. Ver utils/fechas.js.
    const formatFecha = tiempoRelativo;

    const renderNotificacion = (notif) => {
        const { icon, color, bg } = getIconByType(notif.tipo);
        return (
            <ListItem
                key={notif.id}
                className={`${classes.notifItem} ${
                    notif.leida ? classes.notifLeida : classes.notifNoLeida
                }`}
                onClick={() => handleClickNotificacion(notif)}
            >
                <Box display="flex" width="100%">
                    <Box className={classes.iconBox} style={{ background: bg }}>
                        <Box style={{ color }}>{icon}</Box>
                    </Box>
                    <Box flex={1}>
                        <Typography
                            variant="body2"
                            style={{
                                fontWeight: notif.leida ? 400 : 600,
                                marginBottom: 4
                            }}
                        >
                            {notif.titulo}
                        </Typography>
                        {notif.mensaje && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                style={{ display: 'block', marginBottom: 8 }}
                            >
                                {notif.mensaje}
                            </Typography>
                        )}
                        <Typography variant="caption" style={{ color: '#999' }}>
                            {formatFecha(notif.fecha)}
                        </Typography>
                    </Box>
                    <ListItemSecondaryAction>
                        <Tooltip title="Eliminar">
                            <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) => eliminarNotificacion(e, notif.id)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </ListItemSecondaryAction>
                </Box>
            </ListItem>
        );
    };

    const renderEmpty = (mensaje) => (
        <Box className={classes.empty}>
            <NotificationsIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
            <Typography variant="body1" color="text.secondary">
                {mensaje}
            </Typography>
        </Box>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            classes={{ paper: classes.drawer }}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
        >
            <Box className={classes.header}>
                <Box display="flex" alignItems="center" gap={1}>
                    <NotificationsIcon />
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Notificaciones
                    </Typography>
                    {noLeidas > 0 && (
                        <Chip
                            label={noLeidas}
                            size="small"
                            sx={{ background: '#fff', color: 'primary.main', fontWeight: 700 }}
                        />
                    )}
                </Box>
                <IconButton onClick={onClose} style={{ color: '#fff' }} size="large">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Tabs
                value={tabActivo}
                onChange={(_, v) => setTabActivo(v)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
            >
                <Tab label={`No leídas${pendientes.length > 0 ? ` (${pendientes.length})` : ''}`} />
                <Tab label={`Historial${historial.length > 0 ? ` (${historial.length})` : ''}`} />
            </Tabs>

            <Box className={classes.content}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : tabActivo === 0 ? (
                    <>
                        {pendientes.length > 0 && (
                            <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                startIcon={<DoneAllIcon />}
                                onClick={marcarTodasLeidas}
                                sx={{ mb: 1.5, textTransform: 'none' }}
                            >
                                Marcar todas como leídas
                            </Button>
                        )}
                        {pendientes.length === 0
                            ? renderEmpty('No tenés notificaciones pendientes')
                            : <List disablePadding>{pendientes.map(renderNotificacion)}</List>
                        }
                    </>
                ) : (
                    historial.length === 0
                        ? renderEmpty('No hay historial de notificaciones')
                        : <List disablePadding>{historial.map(renderNotificacion)}</List>
                )}
            </Box>
        </Drawer>
    );
};

export default NotificacionesPanel;
